import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import SubHeader from '../../layout/components/SubHeader';
import Pagination from '../../common/Pagination';
import campaignService from '../../../services/campaign.service';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import './BlastResults.css';

const PAGE_SIZE_OPTIONS = [20, 30, 50];

const RESULT_STATUS_META = {
    SUBSCRIBED_SENT: {
        label: 'Sent',
        className: 'is-sent',
        icon: 'bi-check2',
    },
    READ: {
        label: 'Read',
        className: 'is-read',
        icon: 'bi-eye',
    },
    FAILED: {
        label: 'Failed',
        className: 'is-failed',
        icon: 'bi-x-lg',
    },
    UNSUBSCRIBED: {
        label: 'Unsubscribed',
        className: 'is-unsubscribed',
        icon: 'bi-slash-circle',
    },
};

const CAMPAIGN_STATUS_META = {
    COMPLETED: { label: 'Completed', className: 'is-completed' },
    ERROR: { label: 'Error', className: 'is-error' },
    PENDING: { label: 'Pending', className: 'is-pending' },
    IN_PROGRESS: { label: 'In progress', className: 'is-progress' },
};

const formatCount = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) {
        return '0';
    }
    return number.toLocaleString();
};

function BlastResults({
    listPath,
    listLabel = 'Past blasts',
    channelLabel = 'Blast',
    icon = 'bi-bar-chart',
}) {
    const { campaignId } = useParams();
    const navigate = useNavigate();
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);

    const [campaign, setCampaign] = useState(null);
    const [stats, setStats] = useState(null);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(30);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const loadPage = useCallback(
        async (currentPage, currentSize) => {
            if (!clinicId || !campaignId) {
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                const [campaignRes, statsRes, resultsRes] = await Promise.all([
                    campaignService.getCampaignById(clinicId, campaignId),
                    campaignService.getCampaignResultStats(clinicId, campaignId).catch(() => null),
                    campaignService.getCampaignResults(clinicId, campaignId, {
                        page: currentPage,
                        size: currentSize,
                    }),
                ]);

                setCampaign(campaignRes.data || null);
                setStats(statsRes?.data || null);

                const data = resultsRes.data || {};
                setResults(data.content || []);
                setTotalElements(data.totalElements || 0);
                setTotalPages(data.totalPages || 0);
                setPage(data.number ?? currentPage);
                setSize(data.size ?? currentSize);
            } catch (error) {
                console.error('Error loading blast results:', error);
                toast.error('Could not load blast results.');
                setCampaign(null);
                setResults([]);
            } finally {
                setLoading(false);
            }
        },
        [clinicId, campaignId]
    );

    useEffect(() => {
        loadPage(0, size);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clinicId, campaignId]);

    const handleSizeChange = (newSize) => {
        setSize(newSize);
        setPage(0);
        loadPage(0, newSize);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
        loadPage(newPage, size);
    };

    const campaignStatus = CAMPAIGN_STATUS_META[campaign?.status] || {
        label: campaign?.status || 'Unknown',
        className: 'is-unknown',
    };

    const statCards = useMemo(
        () => [
            { key: 'total', label: 'Total', value: stats?.total, icon: 'bi-people' },
            { key: 'sent', label: 'Sent', value: stats?.sent, icon: 'bi-check2-circle' },
            { key: 'read', label: 'Read', value: stats?.read, icon: 'bi-eye' },
            { key: 'failed', label: 'Failed', value: stats?.failed, icon: 'bi-exclamation-triangle' },
            {
                key: 'unsubscribed',
                label: 'Unsubscribed',
                value: stats?.unsubscribed,
                icon: 'bi-slash-circle',
            },
        ],
        [stats]
    );

    return (
        <div className="ecx-page ecx-page--full-width ecx-blast-results-page">
            <SubHeader
                title={campaign?.name || `${channelLabel} results`}
                description={
                    campaign
                        ? `Created ${campaign.createdAt || '—'} · ${formatCount(totalElements)} recipients`
                        : 'Delivery details for this blast'
                }
                icon={icon}
                containerClass="w-100 px-4"
            >
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <Link to={listPath} className="btn btn-sm btn-outline-secondary">
                        <i className="bi bi-arrow-left me-1" />
                        {listLabel}
                    </Link>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => loadPage(page, size)}
                        disabled={loading}
                    >
                        <i className={`bi bi-arrow-clockwise me-1 ${loading ? 'ecx-refresh-spin' : ''}`} />
                        Refresh
                    </button>
                </div>
            </SubHeader>

            <div className="ecx-page-body">
                {loading && !campaign ? (
                    <div className="ecx-page-card ecx-page-panel">
                        <div className="ecx-page-empty py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    </div>
                ) : !campaign ? (
                    <div className="ecx-page-card ecx-page-panel">
                        <div className="ecx-page-empty py-5">
                            <span className="ecx-page-empty__icon">
                                <i className="bi bi-exclamation-circle" />
                            </span>
                            <h5 className="mb-2 text-dark">Blast not found</h5>
                            <p className="mb-3 text-muted">
                                This blast may have been removed or is unavailable.
                            </p>
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => navigate(listPath)}
                            >
                                Back to {listLabel.toLowerCase()}
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="ecx-blast-results__summary mb-3">
                            <div className="ecx-blast-results__summary-main">
                                <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                                    <span
                                        className={`ecx-blast-results__campaign-status ${campaignStatus.className}`}
                                    >
                                        {campaignStatus.label}
                                    </span>
                                    <span className="ecx-page-chip">
                                        <i className="bi bi-tag" />
                                        {campaign.type || channelLabel}
                                    </span>
                                </div>
                                {campaign.description ? (
                                    <p className="text-muted small mb-2">{campaign.description}</p>
                                ) : null}
                                {campaign.contentPath ? (
                                    <div className="ecx-blast-results__message">
                                        <div className="text-muted small mb-1">Message</div>
                                        <div className="ecx-blast-results__message-body">
                                            {campaign.contentPath}
                                        </div>
                                    </div>
                                ) : null}
                            </div>

                            <div className="ecx-blast-results__stat-grid">
                                {statCards.map((card) => (
                                    <div key={card.key} className="ecx-blast-results__stat-card">
                                        <div className="ecx-blast-results__stat-label">
                                            <i className={`bi ${card.icon}`} />
                                            {card.label}
                                        </div>
                                        <div className="ecx-blast-results__stat-value">
                                            {formatCount(card.value)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="ecx-page-card ecx-page-panel ecx-blast-results__panel">
                            <div className="ecx-page-panel__header">
                                <div className="ecx-blast-results__toolbar">
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <h5 className="mb-0 fw-normal text-dark">
                                            Recipient results
                                        </h5>
                                        <span className="ecx-page-chip">
                                            <i className="bi bi-list-ul" />
                                            {formatCount(totalElements)} total
                                        </span>
                                    </div>
                                    <label className="d-flex align-items-center gap-2 mb-0 small text-muted">
                                        Show
                                        <select
                                            className="form-select form-select-sm"
                                            style={{ width: '4.75rem' }}
                                            value={size}
                                            onChange={(e) =>
                                                handleSizeChange(parseInt(e.target.value, 10))
                                            }
                                            disabled={loading}
                                        >
                                            {PAGE_SIZE_OPTIONS.map((option) => (
                                                <option key={option} value={option}>
                                                    {option}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                </div>
                            </div>

                            <div className="ecx-page-panel__body d-flex flex-column p-0">
                                {loading ? (
                                    <div className="ecx-page-empty py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : results.length === 0 ? (
                                    <div className="ecx-page-empty py-5">
                                        <span className="ecx-page-empty__icon">
                                            <i className="bi bi-inbox" />
                                        </span>
                                        <h5 className="mb-2 text-dark">No recipient results yet</h5>
                                        <p className="mb-0 text-muted">
                                            {campaign.status === 'ERROR'
                                                ? 'This blast ended in an error before recipient results were recorded.'
                                                : 'Results will appear here once messages are processed.'}
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="table-responsive ecx-blast-results__table-wrap">
                                            <table className="table table-hover align-middle mb-0 ecx-blast-results__table">
                                                <thead>
                                                    <tr>
                                                        <th className="ps-3">Patient</th>
                                                        <th>Receiver</th>
                                                        <th>Status</th>
                                                        <th className="pe-3">Recorded</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {results.map((row) => {
                                                        const statusMeta =
                                                            RESULT_STATUS_META[row.status] || {
                                                                label: row.status || 'Unknown',
                                                                className: 'is-unknown',
                                                                icon: 'bi-circle',
                                                            };

                                                        return (
                                                            <tr key={row.id}>
                                                                <td className="ps-3">
                                                                    <div className="fw-normal text-dark">
                                                                        {row.patientName || '—'}
                                                                    </div>
                                                                </td>
                                                                <td className="text-muted text-nowrap">
                                                                    {row.receiver || '—'}
                                                                </td>
                                                                <td>
                                                                    <span
                                                                        className={`ecx-blast-results__status ${statusMeta.className}`}
                                                                    >
                                                                        <i
                                                                            className={`bi ${statusMeta.icon}`}
                                                                        />
                                                                        {statusMeta.label}
                                                                    </span>
                                                                </td>
                                                                <td className="text-muted text-nowrap pe-3">
                                                                    {row.createdAt || '—'}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>

                                        {totalPages > 1 && (
                                            <div className="ecx-page-pagination border-top p-3 mt-auto">
                                                <Pagination
                                                    currentPage={page}
                                                    totalPages={totalPages}
                                                    totalElements={totalElements}
                                                    pageSize={size}
                                                    onPageChange={handlePageChange}
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default BlastResults;
